import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  User,
  Mail,
  Phone,
  MapPin,
  CalendarDays,
  ShieldAlert,
  BadgeCheck,
  DollarSign,
  AlertTriangle,
  Lock,
  ShieldCheck,
} from "lucide-react";
import { StatusBadge as ComplianceStatusBadge } from "@/components/ComplianceEditor";
import TwoFactorSetup from "@/components/TwoFactorSetup";

function StatTile({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="card-surface p-4 flex items-start gap-3"
      data-testid={testid}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-clinical-primary/10 text-clinical-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className="label-eyebrow">{label}</div>
        <div className="mt-0.5 text-sm font-medium text-clinical-text break-words">
          {value || <span className="text-clinical-subtext italic">Not set</span>}
        </div>
      </div>
    </div>
  );
}

export default function MyProfile() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [compliance, setCompliance] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/auth/me"),
      api.get("/me/compliance").catch(() => ({ data: { items: [] } })),
    ])
      .then(([meRes, compRes]) => {
        const data = meRes.data;
        setMe(data);
        setForm({
          name: data.name || "",
          phone: data.phone || "",
          address: data.address || "",
          dob: data.dob || "",
          emergency_contact_name: data.emergency_contact_name || "",
          emergency_contact_phone: data.emergency_contact_phone || "",
        });
        setCompliance(compRes.data.items || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", form);
      setMe(data);
      toast.success("Profile updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading profile…
      </div>
    );
  }

  const certs = me?.certifications || [];
  // Show the 2FA setup card when:
  //   • the URL says ?enroll_2fa=1 (user was bounced here by the mandatory-2FA gate), OR
  //   • the user's role requires 2FA and they haven't enrolled yet.
  const params = new URLSearchParams(window.location.search);
  const showTwoFactor = params.get("enroll_2fa") === "1" || me?.requires_2fa_enrollment;

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <div>
        <div className="label-eyebrow">Your account</div>
        <h1 className="mt-1 text-2xl font-heading font-semibold">My profile</h1>
        <p className="mt-1 text-sm text-clinical-subtext">
          Keep your contact info up to date. Some fields are managed by your
          agency admin.
        </p>
      </div>

      {/* 2FA enrollment — required for admins/owners/superadmins before they
          can use the rest of the app. Caregivers can enroll too (optional). */}
      {showTwoFactor && (
        <div className="card-surface p-5 border-l-4 border-l-clinical-primary" data-testid="my-profile-2fa-card">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-clinical-primary/10 text-clinical-primary">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-base font-semibold text-clinical-text">
                {me?.requires_2fa_enrollment ? "Set up two-factor authentication" : "Two-factor authentication"}
              </h2>
              <p className="mt-1 text-sm text-clinical-subtext">
                {me?.requires_2fa_enrollment
                  ? "Your role requires 2FA before you can access the rest of the app. It takes ~60 seconds with any authenticator app (Google Authenticator, Authy, 1Password)."
                  : "Add a second layer of protection to your account."}
              </p>
              <div className="mt-4">
                <TwoFactorSetup
                  onComplete={() => {
                    // After successful enrollment, send the user where they belong.
                    // The fresh user payload (with totp_enabled=true) will be re-fetched
                    // by AuthContext's /auth/me probe on the next page load.
                    const role = (me?.role || "").toLowerCase();
                    if (me?.is_superadmin) {
                      window.location.href = "/platform";
                    } else if (role === "admin") {
                      window.location.href = "/admin";
                    } else if (role === "caregiver") {
                      window.location.href = "/clients";
                    } else {
                      window.location.href = "/";
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Read-only employment summary (admin-managed) */}
      <div>
        <div className="label-eyebrow mb-2 flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Managed by your agency
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <StatTile
            icon={Mail}
            label="Email"
            value={me.email}
            testid="profile-readonly-email"
          />
          <StatTile
            icon={User}
            label="Role"
            value={me.role}
            testid="profile-readonly-role"
          />
          <StatTile
            icon={CalendarDays}
            label="Hire date"
            value={me.hire_date}
            testid="profile-readonly-hire"
          />
          {me.hourly_rate != null && (
            <StatTile
              icon={DollarSign}
              label="Hourly rate"
              value={`$${Number(me.hourly_rate).toFixed(2)}/hr`}
              testid="profile-readonly-rate"
            />
          )}
        </div>
      </div>

      {/* Certifications */}
      <div className="card-surface p-5" data-testid="profile-certs">
        <div className="flex items-center gap-2 mb-3">
          <BadgeCheck className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-sm font-medium">
            Certifications &amp; licenses
          </h2>
        </div>
        {certs.length === 0 ? (
          <p className="text-sm text-clinical-subtext italic">
            No certifications on file. Ask your admin to add them.
          </p>
        ) : (
          <ul className="divide-y divide-clinical-border">
            {certs.map((c, i) => {
              const exp = c.expires_on
                ? new Date(c.expires_on + "T00:00:00")
                : null;
              const daysLeft = exp
                ? Math.ceil((exp.getTime() - Date.now()) / 86400000)
                : null;
              const expired = daysLeft != null && daysLeft < 0;
              const soon = daysLeft != null && daysLeft >= 0 && daysLeft <= 30;
              return (
                <li
                  key={`${c.name}-${c.number}-${c.expires_on}-${i}`}
                  className="py-2.5 flex items-center gap-3"
                  data-testid={`profile-cert-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-clinical-text">
                      {c.name || "—"}
                      {c.number && (
                        <span className="ml-2 text-xs text-clinical-subtext font-normal">
                          {c.number}
                        </span>
                      )}
                    </div>
                    {exp && (
                      <div className="mt-0.5 text-[11px] text-clinical-subtext flex items-center gap-1">
                        Expires {exp.toLocaleDateString()}
                        {(expired || soon) && (
                          <span
                            className={`pill ml-1 ${
                              expired
                                ? "!bg-destructive/10 !text-destructive"
                                : "!bg-amber-100 !text-amber-900"
                            }`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {expired
                              ? `Expired ${Math.abs(daysLeft)}d ago`
                              : `${daysLeft}d left`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Compliance (read-only) */}
      <div className="card-surface p-5" data-testid="profile-compliance">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-sm font-medium">
            Compliance &amp; verification
          </h2>
          <span className="ml-auto text-[11px] text-clinical-subtext">
            <Lock className="inline h-3 w-3" /> Managed by your agency
          </span>
        </div>
        {(compliance || []).length === 0 ? (
          <p className="text-sm text-clinical-subtext italic">
            No compliance items on file. Your agency will add these as part of your onboarding.
          </p>
        ) : (
          <ul className="divide-y divide-clinical-border">
            {compliance.map((c) => (
              <li
                key={c.id}
                className="py-2.5 flex items-center gap-3"
                data-testid={`profile-compliance-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-clinical-text">{c.label}</div>
                  <div className="mt-0.5 text-[11px] text-clinical-subtext">
                    {c.expires_on
                      ? `Expires ${new Date(c.expires_on + "T00:00:00").toLocaleDateString()}`
                      : c.issued_on
                      ? `Issued ${new Date(c.issued_on + "T00:00:00").toLocaleDateString()}`
                      : "—"}
                  </div>
                </div>
                <ComplianceStatusBadge status={c.effective_status} daysLeft={c.days_left} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editable contact info */}
      <div className="card-surface p-5">
        <h2 className="font-heading text-sm font-medium mb-3 flex items-center gap-2">
          <User className="h-4 w-4 text-clinical-primary" />
          Contact info
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-clinical-subtext block mb-1">
              Display name
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-clinical"
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <label className="text-xs text-clinical-subtext block mb-1">
              <Phone className="inline h-3 w-3 mr-1" /> Phone
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 555-123-4567"
              className="input-clinical"
              data-testid="profile-phone-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-clinical-subtext block mb-1">
              <MapPin className="inline h-3 w-3 mr-1" /> Address
            </label>
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Street, city, ZIP"
              className="input-clinical"
              data-testid="profile-address-input"
            />
          </div>
          <div>
            <label className="text-xs text-clinical-subtext block mb-1">
              <CalendarDays className="inline h-3 w-3 mr-1" /> Date of birth
            </label>
            <input
              type="date"
              value={form.dob}
              onChange={(e) => setForm({ ...form, dob: e.target.value })}
              className="input-clinical"
              data-testid="profile-dob-input"
            />
          </div>
        </div>
      </div>

      {/* Emergency contact */}
      <div className="card-surface p-5">
        <h2 className="font-heading text-sm font-medium mb-3 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-destructive" />
          Emergency contact
        </h2>
        <p className="text-xs text-clinical-subtext mb-3">
          The agency will use this if something happens during a shift.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            value={form.emergency_contact_name}
            onChange={(e) =>
              setForm({ ...form, emergency_contact_name: e.target.value })
            }
            placeholder="Name (e.g. Mom)"
            className="input-clinical"
            data-testid="profile-emc-name-input"
          />
          <input
            type="tel"
            value={form.emergency_contact_phone}
            onChange={(e) =>
              setForm({ ...form, emergency_contact_phone: e.target.value })
            }
            placeholder="Phone"
            className="input-clinical"
            data-testid="profile-emc-phone-input"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="profile-save-button"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Save className="h-4 w-4" /> Save changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}
